/**
 * React Query hooks for the workspace's business profiles (T-243).
 * A write refreshes every query through lib/callable.ts, so the list and the form show saved data.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createBusinessProfile,
  deleteBusinessProfile,
  getBusinessProfileLogoUploadUrl,
  listBusinessProfiles,
  updateBusinessProfile,
  type BusinessLogoContentType,
  type BusinessProfile,
  type BusinessProfileChanges,
  type BusinessProfileInput,
} from "../services/naraDoor";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { logProfileError, putLogo } from "../lib/businessProfiles";

/** Pass enabled=false for a non-admin: the callables refuse them, so the page must not ask. */
export function useBusinessProfiles(enabled: boolean) {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.id;

  return useQuery<BusinessProfile[]>({
    queryKey: ["businessProfiles", workspaceId],
    queryFn: async () => {
      try {
        return (await listBusinessProfiles()).profiles;
      } catch (err) {
        logProfileError("listBusinessProfiles", { workspaceId }, err);
        throw err;
      }
    },
    enabled: enabled && !!workspaceId,
    retry: false,
  });
}

export function useCreateBusinessProfile() {
  return useMutation<BusinessProfile, Error, BusinessProfileInput>({
    mutationFn: async (profile) => (await createBusinessProfile(profile)).profile,
  });
}

export function useUpdateBusinessProfile() {
  return useMutation<BusinessProfile, Error, { profileId: string; changes: BusinessProfileChanges }>({
    mutationFn: async ({ profileId, changes }) => (await updateBusinessProfile(profileId, changes)).profile,
  });
}

export function useDeleteBusinessProfile() {
  return useMutation<unknown, Error, string>({ mutationFn: deleteBusinessProfile });
}

/** Get a signed URL, PUT the file to it, then point the profile at the stored file. */
export function useUploadBusinessLogo() {
  return useMutation<BusinessProfile, Error, { profileId: string; file: File; contentType: BusinessLogoContentType }>({
    mutationFn: async ({ profileId, file, contentType }) => {
      const { uploadUrl, storageKey } = await getBusinessProfileLogoUploadUrl(profileId, contentType);
      await putLogo(uploadUrl, file, contentType);
      return (await updateBusinessProfile(profileId, { logo_storage_key: storageKey })).profile;
    },
  });
}
